// Conversation Context - Manages conversation history and navigation

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiClient from '../services/api';
import { ConversationSession } from '../types';

interface ConversationContextType {
  conversations: ConversationSession[];
  currentConversation: ConversationSession | null;
  loading: boolean;
  error: string | null;
  loadConversations: () => Promise<void>;
  selectConversation: (sessionId: string) => Promise<void>;
  createNewConversation: () => Promise<string>;
  deleteConversation: (sessionId: string) => Promise<void>;
  refreshCurrentConversation: (sessionId: string) => Promise<void>;
}

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

export const useConversations = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversations must be used within a ConversationProvider');
  }
  return context;
};

interface ConversationProviderProps {
  children: ReactNode;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({ children }) => {
  const [conversations, setConversations] = useState<ConversationSession[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ConversationSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsertConversation = useCallback((updatedConversation: ConversationSession) => {
    setConversations((prev) => {
      const normalizedConversation: ConversationSession = {
        ...updatedConversation,
        message_count: updatedConversation.messages?.length ?? updatedConversation.message_count,
      };

      const withoutUpdated = prev.filter(
        (conversation) => conversation.session_id !== normalizedConversation.session_id
      );

      return [normalizedConversation, ...withoutUpdated].sort(
        (a, b) => b.updated_at - a.updated_at
      );
    });
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.listSessions(50); // Get last 50 conversations
      const sortedConversations = data.sort((a, b) => b.updated_at - a.updated_at);

      // Backend can return incorrect message_count on list endpoints.
      // Always hydrate the first 5 conversations to ensure accurate counts.
      const conversationsToHydrate = sortedConversations.slice(0, 5);

      if (conversationsToHydrate.length === 0) {
        setConversations(sortedConversations);
        return;
      }

      const hydratedResults = await Promise.allSettled(
        conversationsToHydrate.map((conv) => apiClient.getSession(conv.session_id))
      );

      const hydratedMap = new Map(
        hydratedResults
          .filter((result): result is PromiseFulfilledResult<ConversationSession> => result.status === 'fulfilled')
          .map((result) => [result.value.session_id, result.value])
      );

      const mergedConversations = sortedConversations.map((conv) => {
        const hydrated = hydratedMap.get(conv.session_id);
        if (!hydrated) {
          return conv;
        }

        return {
          ...conv,
          message_count: hydrated.messages ? hydrated.messages.length : conv.message_count,
          messages: hydrated.messages || conv.messages,
          updated_at: hydrated.updated_at || conv.updated_at,
        };
      });

      setConversations(mergedConversations);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
      setError(err.detail || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectConversation = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      const conversation = await apiClient.getSession(sessionId);
      setCurrentConversation(conversation);

      // Update or insert the conversation in the list with latest data.
      upsertConversation(conversation);
    } catch (err: any) {
      console.error('Failed to select conversation:', err);
      setError(err.detail || 'Failed to load conversation');
    }
  }, [upsertConversation]);

  const createNewConversation = useCallback(async (): Promise<string> => {
    try {
      setError(null);
      const newConversation = await apiClient.createSession('webchat');
      setCurrentConversation(newConversation);

      // Add/update in list and keep sorted.
      upsertConversation(newConversation);
      
      return newConversation.session_id;
    } catch (err: any) {
      console.error('Failed to create conversation:', err);
      setError(err.detail || 'Failed to create conversation');
      throw err;
    }
  }, [upsertConversation]);

  const deleteConversation = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      await apiClient.deleteSession(sessionId);
      
      // Remove from list
      setConversations(prev => prev.filter(conv => conv.session_id !== sessionId));
      
      // If it was the current conversation, clear it
      if (currentConversation?.session_id === sessionId) {
        setCurrentConversation(null);
      }
    } catch (err: any) {
      console.error('Failed to delete conversation:', err);
      setError(err.detail || 'Failed to delete conversation');
    }
  }, [currentConversation]);

  const refreshCurrentConversation = useCallback(async (sessionId: string) => {
    try {
      const updated = await apiClient.getSession(sessionId);
      setCurrentConversation(updated);

      // Upsert in list so first message appears without page reload.
      upsertConversation(updated);
    } catch (err: any) {
      console.error('Failed to refresh conversation:', err);
      setError(err.detail || 'Failed to refresh conversation');
    }
  }, [upsertConversation]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const value: ConversationContextType = {
    conversations,
    currentConversation,
    loading,
    error,
    loadConversations,
    selectConversation,
    createNewConversation,
    deleteConversation,
    refreshCurrentConversation,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};